// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsDataTable from '../actions/actionsDataTable';
import FooterStep from '../../modal/components/footers/FooterStep/FooterStep';
import FileUpload from '../../fileUpload/FileUpload';
import HeaderTitle from '../../modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '../../entity/Entity';
import { Modal, ModalContent, ModalBody } from '@/common-utils/components/ui/modal';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';

/**
 *
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.importProps
 * @returns {JSX} ModalImport
 */
function ModalImport({ isOpen, setIsOpen, importProps }) {

    // Toast
    const { toast } = useToast();

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.import.steps';

    // States
    const [step, setStep] = useState(0);
    const [fileUploaded, setFileUploaded] = useState({ numberOfLines: 0, file: null });
    const [error, setError] = useState(null);
    const [fileDetails, setFileDetails] = useState(null);
    const [postImportActionIsLoading, setPostImportActionIsLoading] = useState(false);
    const [showPostImportStep, setShowPostImportStep] = useState(false);
    const [showPostImportErrors, setShowPostImportErrors] = useState(false);

    // Functions
    const onClose = () => {
        setIsOpen(false);
        if (importProps.setLaunchRefetch) {
            importProps.setLaunchRefetch(true);
        }
    };

    // Api calls
    const onSuccess = (response) => {
        if (importProps.renderPostImportErrors && importProps.isFileOnError(response)) {
            setFileDetails(response);
            setShowPostImportErrors(true);
        } else if (importProps.renderPostImport) {
            setFileDetails(response);
            setShowPostImportStep(true);
        } else {
            onClose();
        }

        if (importProps.isFileOnError(response)) {
            toast({
                description: <Entity entity='modal.import.file_errors' />,
                variant: 'danger'
            });
        } else {
            toast({
                description: <Entity entity='modal.import.success' namespace={importProps.namespace} />,
                variant: 'success'
            });
        }
    };
    const onError = (err) => {
        if (importProps.renderPostImportErrors) {
            setError(err);
            setShowPostImportErrors(true);
        } else {
            onClose();
        }

        toast({
            description: <Entity
                entity={`${importProps.entityPrefix || 'modal.import'}.error.${err.response.data.errorCode}`}
                namespace={importProps.namespace}
            />,
            variant: 'danger'
        });
    };
    const submitImport = actionsDataTable.importData(onSuccess, onError);


    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.file_upload`, disabledNext: !fileUploaded.file },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [fileUploaded.file]
    );



    return (
        <Modal open={isOpen} onOpenChange={onClose}>
            <ModalContent className='max-w-4xl'>
                <HeaderTitle title={'modal.import.title'} />
                <ModalBody>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <div className='my-4'>
                            <div className='max-h-[500px] overflow-y-auto py-4'>
                                {importProps.explanations()}
                            </div>
                            <FileUpload
                                setFileUploaded={setFileUploaded}
                                fileExampleProperties={importProps.fileExampleProperties}
                                uploadProperties={importProps.uploadProperties}
                                manageFileData={importProps.countLines}
                                fileRemoved={() => {
                                    setFileUploaded({ numberOfLines: 0, file: null });
                                }}
                            />
                        </div>
                    }
                    {
                        step === 1 &&
                        <>
                            {
                                !showPostImportStep && !showPostImportErrors &&
                                <div className='my-4'>
                                    {importProps.confirmation(fileUploaded.numberOfLines)}
                                </div>
                            }
                            {
                                showPostImportStep &&
                                <div className='my-4'>
                                    {importProps.renderPostImport(
                                        fileDetails,
                                        { onClose, setPostImportActionIsLoading }
                                    )}
                                </div>
                            }
                            {
                                showPostImportErrors &&
                                <div className='my-4'>
                                    {importProps.renderPostImportErrors(fileDetails, error)}
                                </div>
                            }
                        </>
                    }

                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    isLoading={submitImport.isLoading || postImportActionIsLoading}
                    onValidate={() => {
                        submitImport.mutate({
                            importApi: importProps.apiUrl,
                            pathParams: importProps.pathParams,
                            file: fileUploaded.file
                        });
                    }}
                    disableValidation={disabledNext}
                    isError={showPostImportStep || showPostImportErrors}
                />
            </ModalContent>
        </Modal>
    );
}

ModalImport.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    importProps: PropTypes.object
};

export default ModalImport;